import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../lib/api";
import { useProfile } from "../context/ProfileContext";

// Shared round logic used by every mini-game.
export function useGameRound({ gameType, tables, count = 10, choices = 4 }) {
  const { profile, refresh } = useProfile();
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [feedback, setFeedback] = useState(null); // { correct, correctAnswer, chosen }
  const [locked, setLocked] = useState(false);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const logRef = useRef([]);
  const startRef = useRef(Date.now());
  const timerRef = useRef(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    const data = await api.nextQuestions({
      profile_id: profile.id, tables, count, choices,
    });
    setQuestions(data.questions);
    setIndex(0); setScore(0); setStreak(0); setBestStreak(0);
    setFeedback(null); setLocked(false); setFinished(false); setResult(null);
    logRef.current = [];
    startRef.current = Date.now();
    setLoading(false);
  }, [profile, JSON.stringify(tables), count, choices]);

  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const current = questions[index];

  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      const res = await api.submitRound({
        profile_id: profile.id, game_type: gameType, answers: logRef.current,
      });
      setResult(res);
      setFinished(true);
      refresh();
    } finally { setSubmitting(false); }
  }, [profile, gameType, refresh]);

  const answer = useCallback((value, correctAnswer = current?.product) => {
    if (locked || !current) return;
    setLocked(true);
    const rt = Date.now() - startRef.current;
    const correct = Number(value) === correctAnswer;
    logRef.current.push({ a: current.a, b: current.b, correct, response_ms: rt });

    if (correct) {
      setScore((s) => s + 1);
      setStreak((st) => { const n = st + 1; setBestStreak((b) => Math.max(b, n)); return n; });
    } else {
      setStreak(0);
    }
    setFeedback({ correct, correctAnswer, chosen: Number(value) });

    const delay = correct ? 750 : 1350;
    timerRef.current = setTimeout(() => {
      if (index + 1 >= questions.length) {
        submit();
      } else {
        setIndex((i) => i + 1);
        setFeedback(null);
        setLocked(false);
        startRef.current = Date.now();
      }
    }, delay);
  }, [locked, current, index, questions.length, submit]);

  const restart = useCallback(() => setReloadKey((k) => k + 1), []);

  return {
    profile, loading, questions, current, index, total: questions.length,
    score, streak, bestStreak, feedback, locked, finished, result, submitting,
    answer, restart,
  };
}
